<script lang="ts">
    import Fa from 'svelte-fa'
    import { faCircleCheck, faFloppyDisk, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
    import { getBlockDefinition, getEffectiveBlockWidth } from '../blocks/block-registry'
    import DocumentHeader from '../shared/document-header.svelte'
    import DocumentFooter from '../shared/document-footer.svelte'
    import type { PlacedBlock } from '../types/placed-block'
    import type { DocumentHeaderFooter } from '../types/document-header-footer'

    /**
     * Componente de preenchimento do formulário, desacoplado do construtor.
     * Recebe `title`, `blocks`, `headerFooter` e `public` — pode futuramente ser montado dentro de
     * uma página pública de preenchimento (ex: link enviado ao cliente), sem
     * depender de nada do builder/admin. Quando `public` é true, campos marcados
     * como "somente leitura para o usuário" ficam bloqueados para quem preenche.
     */
    export let title: string
    export let blocks: PlacedBlock[] = []
    export let headerFooter: DocumentHeaderFooter
    let isPublic = false
    export { isPublic as public }

    let answers: Record<string, any> = buildInitialAnswers()
    let submitted = false
    let validationError = ''
    let draftSavedAt = ''

    function buildInitialAnswers(): Record<string, any> {
        const initial: Record<string, any> = {}
        for (const block of blocks) {
            const definition = getBlockDefinition(block.type)
            initial[block.id] = definition ? definition.createDefaultValue() : null
        }
        return initial
    }

    function isFieldLocked(block: PlacedBlock) {
        return isPublic && block.readOnlyForPublic
    }

    function isAnswerEmpty(block: PlacedBlock) {
        const definition = getBlockDefinition(block.type)
        return definition ? definition.isValueEmpty(answers[block.id]) : false
    }

    // O template é "congelado" junto da resposta: se o formulário for editado depois,
    // esta submissão continua representando a versão exata que a pessoa de fato preencheu.
    function buildSubmission(status: 'draft' | 'submitted') {
        return {
            template: {
                title,
                headerFooter,
                blocks: blocks.map((block) => ({
                    id: block.id,
                    type: block.type,
                    label: block.label,
                    data: block.data,
                    required: block.required,
                    readOnlyForPublic: block.readOnlyForPublic,
                    width: getEffectiveBlockWidth(block),
                })),
            },
            answers,
            status,
            savedAt: new Date().toISOString(),
        }
    }

    function onSubmit() {
        const missing = blocks.filter((block) => block.required && !isFieldLocked(block) && isAnswerEmpty(block))
        if (missing.length > 0) {
            validationError = `Preencha os campos obrigatórios: ${missing.map((block) => block.label).join(', ')}`
            return
        }
        validationError = ''
        draftSavedAt = ''

        const submission = buildSubmission('submitted')
        console.log('Envio do formulário (estrutura + respostas):', JSON.stringify(submission, null, 2))
        submitted = true
    }

    function onSaveDraft() {
        // Rascunho: ignora a validação de obrigatórios de propósito, para permitir salvar parcialmente.
        const submission = buildSubmission('draft')
        console.log('Preenchimento do formulário (estrutura + respostas parciais):', JSON.stringify(submission, null, 2))
        draftSavedAt = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }

    function answerAgain() {
        answers = buildInitialAnswers()
        validationError = ''
        draftSavedAt = ''
        submitted = false
    }
</script>

<div class="form-viewer">
    <div class="viewer-page">
        <DocumentHeader header={headerFooter.header} />

        {#if submitted}
            <div class="viewer-card viewer-submitted">
                <Fa icon={faCircleCheck} size="2x" />
                <h2>Respostas enviadas</h2>
                <p>Modo de teste: nenhuma resposta foi enviada a um servidor.</p>
                <button type="button" class="btn-secondary" on:click={answerAgain}>Responder novamente</button>
            </div>
        {:else if blocks.length === 0}
            <div class="viewer-card">
                <p class="viewer-empty">Este formulário ainda não possui nenhum campo.</p>
            </div>
        {:else}
            <form on:submit|preventDefault={onSubmit}>
                <div class="viewer-grid">
                    {#each blocks as block (block.id)}
                        <div class="viewer-card" style="grid-column: span {getEffectiveBlockWidth(block)}">
                            <svelte:component
                                this={getBlockDefinition(block.type)?.viewComponent}
                                label={block.label}
                                data={block.data}
                                required={block.required}
                                readOnly={isFieldLocked(block)}
                                bind:value={answers[block.id]}
                            />
                        </div>
                    {/each}
                </div>

                {#if validationError}
                    <div class="viewer-error">
                        <Fa icon={faTriangleExclamation} />
                        <span>{validationError}</span>
                    </div>
                {/if}

                <DocumentFooter footer={headerFooter.footer} />

                <div class="viewer-actions">
                    <button type="button" class="btn-secondary" on:click={onSaveDraft}>
                        <Fa icon={faFloppyDisk} />
                        <span>Salvar preenchimento</span>
                    </button>
                    <button type="submit" class="btn-primary">Enviar respostas</button>
                </div>

                {#if draftSavedAt}
                    <p class="draft-note">Preenchimento salvo às {draftSavedAt} (modo de teste, apenas no console).</p>
                {/if}
            </form>
        {/if}
    </div>
</div>

<style>
    .form-viewer {
        min-height: 100%;
        display: flex;
        justify-content: center;
        padding: 40px 20px;
        box-sizing: border-box;
    }
    .viewer-page {
        width: 100%;
        max-width: 990px;
    }
    .viewer-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
    }
    .viewer-card {
        background: #fff;
        border-radius: 12px;
        padding: 20px 22px;
        min-width: 0;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }
    .viewer-empty {
        margin: 0;
        color: #868e96;
        font-size: 0.9rem;
    }
    .viewer-error {
        display: flex;
        align-items: center;
        gap: 10px;
        background: #fff5f5;
        border: 1px solid #ffc9c9;
        color: #c92a2a;
        border-radius: 10px;
        padding: 12px 16px;
        margin-bottom: 14px;
        font-size: 0.85rem;
    }
    .viewer-actions {
        display: flex;
        gap: 10px;
        margin-top: 20px;
    }
    .btn-primary {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: none;
        border-radius: 8px;
        padding: 10px 20px;
        font-size: 0.9rem;
        font-weight: 500;
        background: var(--cs2-accent, #4263eb);
        color: #fff;
        cursor: pointer;
    }
    .btn-primary:hover {
        background: #364fc7;
    }
    .draft-note {
        margin: 8px 0 0;
        font-size: 0.78rem;
        color: #2f9e44;
    }
    .viewer-submitted {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        text-align: center;
        padding: 48px 24px;
        color: #2f9e44;
    }
    .viewer-submitted h2 {
        margin: 0;
        font-size: 1.15rem;
        color: #212529;
    }
    .viewer-submitted p {
        margin: 0;
        font-size: 0.85rem;
        color: #868e96;
    }
    .btn-secondary {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #dee2e6;
        background: #fff;
        color: #495057;
        border-radius: 8px;
        padding: 8px 16px;
        font-size: 0.85rem;
        cursor: pointer;
    }
    .btn-secondary:hover {
        background: #f8f9fa;
    }
    .viewer-submitted .btn-secondary {
        margin-top: 8px;
    }

    @media (max-width: 700px) {
        .viewer-grid {
            grid-template-columns: 1fr;
        }
        .viewer-card {
            grid-column: 1 / -1 !important;
        }
    }
</style>
